/**
 * CCS Savings Bar — First-time buyer welcome flow
 *
 * Eligibility: visitor arrived via ?ref=welcome OR customer.orders_count === 0
 * Shows a sticky savings bar once cart has items, summarising the 5% CLEANCARE5
 * discount + free shipping saving.
 *
 * Integration note:
 *   This file listens for the custom event `ccs:cart:updated` which should be
 *   dispatched from ccsCartDrawerRefresh() in ccs-atc.liquid after every
 *   successful /cart.js fetch. Until that event is wired up, the bar self-polls
 *   /cart.js on initialisation and after its own add-to-cart events.
 *
 * JS cost: ~3KB minified. Interaction-only except for a single /cart.js fetch
 * on first-time buyer pages. Zero cost on all other pages (early-return guard).
 */
(function () {
  'use strict';

  /* ─── Constants ─────────────────────────────────────────────── */
  var DISCOUNT_CODE     = 'CLEANCARE5';
  var DISCOUNT_RATE     = 0.05;
  var SHIPPING_SAVINGS  = 999;   /* cents — flat $9.99 */
  var SCROLL_THROTTLE   = 150;   /* ms */

  /* ─── Helpers ────────────────────────────────────────────────── */

  /**
   * Format cents integer to "$X.XX" string.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Lightweight scroll-event throttle using timestamps.
   * @param {Function} fn
   * @param {number} limit  ms between allowed calls
   * @returns {Function}
   */
  function throttle(fn, limit) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last >= limit) {
        last = now;
        fn.apply(this, arguments);
      }
    };
  }

  /* ─── First-time buyer detection ─────────────────────────────── */

  /**
   * Returns true if this visitor qualifies as a first-time buyer.
   * Two signals used together so either source is enough:
   *   1. localStorage flag set by ?ref=welcome landing
   *   2. data-customer-orders="0" on the bar element (set by Liquid)
   *
   * If the customer has placed orders we clear the localStorage flag so the
   * bar never reappears for returning customers regardless of URL params.
   */
  function isFirstTimeBuyer(barEl) {
    var ordersAttr = barEl ? barEl.getAttribute('data-customer-orders') : null;
    var orderCount = ordersAttr !== null ? parseInt(ordersAttr, 10) : -1;

    /* Logged-in returning customer — suppress unconditionally */
    if (orderCount > 0) {
      try { window.ccsLS.del('ccs_first_time'); } catch (e) {}
      return false;
    }

    /* Mark from URL */
    if (window.location.search.indexOf('ref=welcome') !== -1) {
      try { window.ccsLS.set('ccs_first_time', 'true'); } catch (e) {}
    }

    /* Check localStorage flag */
    var flag = false;
    try { flag = window.ccsLS.get('ccs_first_time') === 'true'; } catch (e) {}

    /* Logged-in, 0 orders customer also qualifies (first order not yet placed) */
    if (orderCount === 0) return true;

    return flag;
  }

  /* ─── Price calculations ─────────────────────────────────────── */

  /**
   * Derive all display values from a raw Shopify cart object.
   * Prices in and out are in cents.
   *
   * @param {Object} cart  Raw response from /cart.js
   * @returns {Object}
   */
  function calculateSavings(cart) {
    var originalTotal   = cart.total_price;                            /* cents */
    var discountAmount  = Math.round(originalTotal * DISCOUNT_RATE);   /* cents */
    var discountedTotal = originalTotal - discountAmount;              /* cents */
    var totalSavings    = discountAmount;                              /* cents — 5% only; no fabricated free-shipping saving */

    return {
      itemCount:        cart.item_count,
      originalTotal:    originalTotal,
      discountAmount:   discountAmount,
      discountedTotal:  discountedTotal,
      shippingSavings:  SHIPPING_SAVINGS,
      totalSavings:     totalSavings
    };
  }

  /* ─── DOM updates ────────────────────────────────────────────── */

  /**
   * Push calculated savings values into the bar's display elements.
   * All queries are scoped to the bar element to avoid collisions.
   *
   * @param {Element} barEl
   * @param {Object}  savings  Output of calculateSavings()
   */
  function updateBarDisplay(barEl, savings) {
    var countEl    = barEl.querySelector('.ccs-savings-count');
    var wasEl      = barEl.querySelector('.ccs-savings-was');
    var nowEl      = barEl.querySelector('.ccs-savings-now');
    var totalEl    = barEl.querySelector('.ccs-savings-total');
    var shippingEl = barEl.querySelector('.ccs-savings-shipping');

    if (countEl)    countEl.textContent    = savings.itemCount;
    if (wasEl)      wasEl.textContent      = formatMoney(savings.originalTotal);
    if (nowEl)      nowEl.textContent      = formatMoney(savings.discountedTotal);
    if (totalEl)    totalEl.textContent    = formatMoney(savings.totalSavings);
    /* shipping line is static honest copy (CLEANCARE5) — intentionally not overwritten */
  }

  /**
   * Show or hide the bar based on cart item count.
   *
   * @param {Element} barEl
   * @param {number}  itemCount
   */
  function updateBarVisibility(barEl, itemCount) {
    if (itemCount > 0) {
      barEl.classList.add('ccs-savings-visible');
    } else {
      barEl.classList.remove('ccs-savings-visible');
    }
  }

  /* ─── Cart fetch ─────────────────────────────────────────────── */

  /**
   * Fetch /cart.js and update the bar.
   * Called on init, on `ccs:cart:updated`, and after checkout button interaction.
   *
   * @param {Element} barEl
   */
  function refreshSavingsBar(barEl) {
    fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var savings = calculateSavings(cart);
        updateBarDisplay(barEl, savings);
        updateBarVisibility(barEl, savings.itemCount);
      })
      .catch(function () {
        /* Network error: keep bar in current state, no change */
      });
  }

  /* ─── Mobile compact scroll behaviour ───────────────────────── */

  /**
   * Wire up scroll-based compact toggling on mobile (<= 768px).
   * Collapses bar on scroll-down, restores on scroll-up.
   * Bar tap always restores.
   *
   * @param {Element} barEl
   */
  function initScrollCollapse(barEl) {
    var lastScrollY = window.pageYOffset || document.documentElement.scrollTop;

    var onScroll = throttle(function () {
      /* Only run on mobile viewport */
      if (window.innerWidth > 768) {
        barEl.classList.remove('ccs-savings-compact');
        return;
      }

      var currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      var scrollingDown  = currentScrollY > lastScrollY;
      lastScrollY        = currentScrollY;

      if (scrollingDown) {
        barEl.classList.add('ccs-savings-compact');
      } else {
        barEl.classList.remove('ccs-savings-compact');
      }
    }, SCROLL_THROTTLE);

    window.addEventListener('scroll', onScroll, { passive: true });

    /* Tap on bar always restores full view */
    barEl.addEventListener('click', function () {
      barEl.classList.remove('ccs-savings-compact');
    });

    /* Store reference so we can remove on nav-away */
    barEl._ccsSavingsScrollHandler = onScroll;
  }

  /* ─── Discount banner dismiss ────────────────────────────────── */

  /**
   * Handle the top-of-page discount banner (separate from the sticky bar).
   * Dismissal is persisted to sessionStorage so it stays gone on reload
   * within the same browser tab session.
   *
   * @param {Element} bannerEl
   */
  function initDiscountBanner(bannerEl) {
    /* On load: honour prior dismissal */
    var dismissed = false;
    try {
      dismissed = window.ccsSS.get('ccs_banner_dismissed') === 'true';
    } catch (e) {}

    if (dismissed) {
      bannerEl.style.display = 'none';
      return;
    }

    /* Wire close button */
    var closeBtn = bannerEl.querySelector('.ccs-discount-banner-close');
    if (!closeBtn) return;

    closeBtn.addEventListener('click', function () {
      bannerEl.style.display = 'none';
      try {
        window.ccsSS.set('ccs_banner_dismissed', 'true');
      } catch (e) {}
    });
  }

  /* ─── Checkout button ────────────────────────────────────────── */

  /**
   * Route the checkout button to /checkout with the discount code pre-applied.
   *
   * @param {Element} barEl
   */
  function initCheckoutButton(barEl) {
    var checkoutBtn = barEl.querySelector('.ccs-savings-checkout');
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener('click', function () {
      window.location.href = '/checkout?discount=' + DISCOUNT_CODE;
    });
  }

  /* ─── Memory leak prevention ─────────────────────────────────── */

  /**
   * Remove scroll listener when the user navigates away (SPA-style navigation
   * or Turbolinks if ever added). Safe no-op if handler was never attached.
   *
   * @param {Element} barEl
   */
  function teardown(barEl) {
    if (barEl._ccsSavingsScrollHandler) {
      window.removeEventListener('scroll', barEl._ccsSavingsScrollHandler);
      barEl._ccsSavingsScrollHandler = null;
    }
  }

  /* ─── Initialisation ─────────────────────────────────────────── */

  /**
   * Entry point. Aborts early if:
   *   - The bar element is not present in the DOM
   *   - The visitor is not a first-time buyer
   */
  function init() {
    var barEl = document.querySelector('.ccs-savings-bar');
    if (!barEl) return;

    /* First-time buyer guard — exits for all returning customers */
    if (!isFirstTimeBuyer(barEl)) return;

    /* Discount banner (may be on the same page, separate element) */
    var bannerEl = document.querySelector('.ccs-discount-banner');
    if (bannerEl) initDiscountBanner(bannerEl);

    /* Checkout button */
    initCheckoutButton(barEl);

    /* Mobile scroll collapse */
    initScrollCollapse(barEl);

    /* Initial cart state */
    refreshSavingsBar(barEl);

    /* React to cart changes dispatched by ccsCartDrawerRefresh()
     * (ccs-atc.liquid should dispatch this event after every /cart.js fetch)  */
    document.addEventListener('ccs:cart:updated', function (event) {
      /* If the event carries a cart payload, use it directly to avoid a
       * redundant network request. Otherwise fall back to fetching /cart.js. */
      if (event.detail && typeof event.detail.item_count === 'number') {
        var savings = calculateSavings(event.detail);
        updateBarDisplay(barEl, savings);
        updateBarVisibility(barEl, savings.itemCount);
      } else {
        refreshSavingsBar(barEl);
      }
    });

    /* Teardown on page hide (back-forward cache, Turbolinks, etc.) */
    window.addEventListener('pagehide', function () {
      teardown(barEl);
    });
  }

  /* Run after DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
